<script setup>
/**
 * @fileoverview Share File Explorer Component
 * @description File browser for share portal (external users)
 * @example
 * <ShareFileExplorer :share-id="shareId" />
 */
import { ref, computed, onMounted, watch } from 'vue'
import {
  RefreshCw,
  FolderPlus,
  ChevronRight,
  Home,
  Loader2,
  AlertCircle,
  Folder,
  File,
  Download,
  Trash2,
  Upload,
  CheckCircle2,
  X
} from 'lucide-vue-next'
import axios from 'axios'
import { Alert, AlertDescription, Button } from '@/components/ui'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import {
  listShareFiles,
  getShareFileDownloadUrl,
  getShareFileUploadUrl,
  createShareDirectory,
  deleteShareFile
} from '@/api/sharePortal'

const props = defineProps({
  shareId: {
    type: String,
    required: true
  },
  initialPath: {
    type: String,
    default: '/'
  }
})

const files = ref([])
const currentPath = ref(props.initialPath)
const isLoading = ref(false)
const error = ref(null)
const showCreateFolderDialog = ref(false)
const newFolderName = ref('')
const isCreatingFolder = ref(false)
const folderError = ref(null)
const showDeleteDialog = ref(false)
const pendingDeleteItem = ref(null)

// Upload state
const isDragOver = ref(false)
const fileInputRef = ref(null)
const uploadQueue = ref([])
const isUploading = computed(() => uploadQueue.value.some(item => item.status === 'uploading'))
const maxFileSize = 104857600 // 100MB

/**
 * Parse path into breadcrumb segments
 */
const breadcrumbs = computed(() => {
  if (!currentPath.value || currentPath.value === '/') {
    return [{ name: 'Root', path: '/' }]
  }

  const segments = currentPath.value.split('/').filter(Boolean)
  const crumbs = [{ name: 'Root', path: '/' }]

  let accumulatedPath = ''
  for (const segment of segments) {
    accumulatedPath += `/${segment}`
    crumbs.push({
      name: segment,
      path: accumulatedPath
    })
  }

  return crumbs
})

/**
 * Load files for current path
 */
async function loadFiles() {
  isLoading.value = true
  error.value = null

  try {
    const result = await listShareFiles(props.shareId, currentPath.value)
    files.value = result.files || result || []
  } catch (err) {
    error.value = err.message || 'Failed to load files'
  } finally {
    isLoading.value = false
  }
}

/**
 * Navigate to a path
 */
function navigateTo(path) {
  if (!path || path === '/') {
    currentPath.value = '/'
  } else {
    currentPath.value = path.startsWith('/') ? path : `/${path}`
    if (currentPath.value.endsWith('/') && currentPath.value !== '/') {
      currentPath.value = currentPath.value.slice(0, -1)
    }
  }
  loadFiles()
}

/**
 * Handle folder click
 */
function handleFolderClick(folder) {
  navigateTo(folder.path)
}

/**
 * Handle file download
 */
async function handleDownload(file) {
  try {
    const result = await getShareFileDownloadUrl(props.shareId, file.path)
    // window.open(result.url || result.download_url, '_blank')
    navigator.clipboard.writeText(result.download_url)
  } catch (err) {
    error.value = `Failed to fetch download url for ${file.name}`
  }
}

/**
 * Handle file/folder deletion
 */
async function handleDelete(item) {
  pendingDeleteItem.value = item
  showDeleteDialog.value = true
}

async function confirmDelete() {
  if (!pendingDeleteItem.value) return
  const item = pendingDeleteItem.value
  try {
    await deleteShareFile(props.shareId, item.path)
    loadFiles()
  } catch (err) {
    error.value = `Failed to delete ${item.name}`
  } finally {
    showDeleteDialog.value = false
    pendingDeleteItem.value = null
  }
}

/**
 * Open create folder dialog
 */
function openCreateFolderDialog() {
  newFolderName.value = ''
  folderError.value = null
  showCreateFolderDialog.value = true
}

/**
 * Create new folder
 */
async function createNewFolder() {
  if (!newFolderName.value.trim()) {
    folderError.value = 'Folder name is required'
    return
  }

  isCreatingFolder.value = true
  folderError.value = null

  try {
    const newPath =
      currentPath.value === '/'
        ? `/${newFolderName.value.trim()}`
        : `${currentPath.value}/${newFolderName.value.trim()}`

    await createShareDirectory(props.shareId, newPath)
    showCreateFolderDialog.value = false
    newFolderName.value = ''
    setTimeout(() => loadFiles(), 500)
  } catch (err) {
    folderError.value = err.message || 'Failed to create folder'
  } finally {
    isCreatingFolder.value = false
  }
}

/**
 * Format file size
 */
function formatSize(bytes) {
  if (!bytes) {
    return '-'
  }
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  while (bytes >= 1024 && i < units.length - 1) {
    bytes /= 1024
    i++
  }
  return `${bytes.toFixed(1)} ${units[i]}`
}

/**
 * Check if item is a folder
 */
function isFolder(item) {
  return item.is_folder || item.type === 'folder' || item.type === 'directory'
}

// ===================
// FILE UPLOAD METHODS
// ===================

/**
 * Format file size
 */
function formatUploadSize(bytes) {
  const units = ['B', 'KB', 'MB', 'GB']
  let i = 0
  while (bytes >= 1024 && i < units.length - 1) {
    bytes /= 1024
    i++
  }
  return `${bytes.toFixed(1)} ${units[i]}`
}

/**
 * Trigger file input click
 */
function triggerFileInput() {
  fileInputRef.value?.click()
}

/**
 * Handle file input change
 */
function handleFileInputChange(event) {
  const files = event.target.files
  if (files && files.length > 0) {
    addFilesToQueue(files)
  }
  // Reset input
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

/**
 * Handle drag over
 */
function handleDragOver(event) {
  event.preventDefault()
  isDragOver.value = true
}

/**
 * Handle drag leave
 */
function handleDragLeave() {
  isDragOver.value = false
}

/**
 * Handle file drop
 */
function handleDrop(event) {
  event.preventDefault()
  isDragOver.value = false
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    addFilesToQueue(files)
  }
}

/**
 * Add files to upload queue
 */
function addFilesToQueue(files) {
  const fileArray = Array.from(files)

  for (const file of fileArray) {
    // Validate file size
    if (file.size > maxFileSize) {
      error.value = `File "${file.name}" exceeds ${formatUploadSize(maxFileSize)} limit`
      continue
    }

    // Add to queue
    const queueItem = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      progress: 0,
      status: 'pending',
      error: null
    }
    uploadQueue.value.push(queueItem)
  }

  // Start uploading
  processQueue()
}

/**
 * Process upload queue
 */
async function processQueue() {
  const pendingItems = uploadQueue.value.filter(item => item.status === 'pending')

  for (const item of pendingItems) {
    await uploadFile(item)
  }
}

/**
 * Upload a single file
 */
async function uploadFile(queueItem) {
  queueItem.status = 'uploading'

  try {
    // Get upload URL
    const uploadPath = currentPath.value === '/' ? '' : currentPath.value
    const result = await getShareFileUploadUrl(
      props.shareId,
      `${uploadPath}/${queueItem.file.name}`
    )

    // Upload to S3
    await axios.put(result.url || result.upload_url, queueItem.file, {
      headers: {
        'Content-Type': queueItem.file.type || 'application/octet-stream'
      },
      onUploadProgress: progressEvent => {
        if (progressEvent.total) {
          queueItem.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        }
      }
    })

    queueItem.status = 'complete'
    queueItem.progress = 100

    // Reload files after upload
    setTimeout(() => loadFiles(), 500)
  } catch (err) {
    queueItem.status = 'error'
    queueItem.error = err.message || 'Upload failed'
  }
}

/**
 * Remove item from upload queue
 */
function removeFromQueue(queueItem) {
  const index = uploadQueue.value.findIndex(item => item.id === queueItem.id)
  if (index !== -1) {
    uploadQueue.value.splice(index, 1)
  }
}

/**
 * Clear completed uploads
 */
function clearCompleted() {
  uploadQueue.value = uploadQueue.value.filter(item => item.status !== 'complete')
}

onMounted(() => {
  loadFiles()
})

watch(
  () => props.shareId,
  () => {
    currentPath.value = props.initialPath
    loadFiles()
  }
)
</script>

<template>
  <div class="w-full space-y-4">
    <!-- Header with breadcrumbs and actions -->
    <div class="flex items-center justify-between gap-4">
      <!-- Breadcrumb navigation -->
      <nav class="flex items-center gap-1 flex-1 overflow-x-auto" aria-label="File path">
        <button
          v-for="(crumb, index) in breadcrumbs"
          :key="crumb.path"
          @click="navigateTo(crumb.path)"
          class="flex items-center gap-1 px-2 py-1 text-sm font-medium rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
          :class="
            index === breadcrumbs.length - 1
              ? 'text-gray-900 dark:text-gray-100'
              : 'text-gray-600 dark:text-gray-400'
          "
        >
          <Home v-if="index === 0" class="w-4 h-4" />
          <span>{{ crumb.name }}</span>
          <ChevronRight v-if="index < breadcrumbs.length - 1" class="w-4 h-4 text-gray-400" />
        </button>
      </nav>

      <!-- Action buttons -->
      <div class="flex items-center gap-2 flex-shrink-0">
        <Button variant="outline" size="sm" @click="loadFiles" :disabled="isLoading">
          <RefreshCw :class="['w-4 h-4', isLoading && 'animate-spin']" />
        </Button>
        <Button variant="default" size="sm" @click="openCreateFolderDialog" :disabled="isLoading">
          <FolderPlus class="w-4 h-4 mr-1.5" />
          New Folder
        </Button>
      </div>
    </div>

    <!-- Error state -->
    <Alert v-if="error" variant="destructive">
      <AlertCircle class="w-4 h-4" />
      <AlertDescription>
        {{ error }}
        <Button variant="link" size="sm" @click="loadFiles" class="ml-2">Try again</Button>
      </AlertDescription>
    </Alert>

    <!-- Loading state -->
    <div v-if="isLoading" class="flex justify-center py-12">
      <Loader2 class="w-8 h-8 animate-spin text-indigo-600" />
    </div>

    <!-- File Upload Section -->
    <div v-if="!isLoading" class="space-y-3">
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">Upload Files</h3>

      <!-- Hidden file input -->
      <input
        ref="fileInputRef"
        type="file"
        multiple
        class="hidden"
        @change="handleFileInputChange"
      />

      <!-- Drag and Drop Zone -->
      <div
        class="border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer"
        :class="
          isDragOver
            ? 'border-indigo-500 bg-indigo-50 dark:border-indigo-400 dark:bg-indigo-900/20'
            : 'border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600'
        "
        @click="triggerFileInput"
        @dragover="handleDragOver"
        @dragleave="handleDragLeave"
        @drop="handleDrop"
      >
        <Upload
          class="w-10 h-10 mx-auto mb-3"
          :class="isDragOver ? 'text-indigo-500' : 'text-gray-400'"
        />
        <p class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
          Drop files here or click to browse
        </p>
        <p class="text-xs text-gray-500 dark:text-gray-400">
          Maximum file size: {{ formatUploadSize(maxFileSize) }}
        </p>
      </div>

      <!-- Upload Queue -->
      <div v-if="uploadQueue.length > 0" class="space-y-2">
        <!-- Queue header -->
        <div class="flex items-center justify-between">
          <h4 class="text-sm font-medium text-gray-900 dark:text-gray-100">
            Upload Queue ({{ uploadQueue.length }})
          </h4>
          <Button
            v-if="uploadQueue.some(item => item.status === 'complete')"
            variant="ghost"
            size="sm"
            @click="clearCompleted"
          >
            Clear Completed
          </Button>
        </div>

        <!-- Queue items -->
        <div class="space-y-2">
          <div
            v-for="item in uploadQueue"
            :key="item.id"
            class="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
          >
            <div class="flex items-center justify-between gap-3">
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <!-- Status icon -->
                <Loader2
                  v-if="item.status === 'uploading'"
                  class="w-5 h-5 text-blue-500 animate-spin flex-shrink-0"
                />
                <CheckCircle2
                  v-else-if="item.status === 'complete'"
                  class="w-5 h-5 text-emerald-500 flex-shrink-0"
                />
                <AlertCircle
                  v-else-if="item.status === 'error'"
                  class="w-5 h-5 text-red-500 flex-shrink-0"
                />
                <Upload v-else class="w-5 h-5 text-gray-400 flex-shrink-0" />

                <div class="flex-1 min-w-0">
                  <p class="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {{ item.file.name }}
                  </p>
                  <p class="text-xs text-gray-500 dark:text-gray-400">
                    {{ formatUploadSize(item.file.size) }}
                  </p>
                  <p v-if="item.error" class="text-xs text-red-600 mt-1">
                    {{ item.error }}
                  </p>
                </div>
              </div>

              <!-- Remove button -->
              <Button variant="ghost" size="sm" @click="removeFromQueue(item)">
                <X class="w-4 h-4" />
              </Button>
            </div>

            <!-- Progress bar -->
            <div v-if="item.status === 'uploading'" class="mt-2">
              <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div
                  class="bg-indigo-600 h-1.5 rounded-full transition-all"
                  :style="{ width: `${item.progress}%` }"
                />
              </div>
              <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ item.progress }}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Separator -->
    <hr v-if="!isLoading" class="border-gray-200 dark:border-gray-700" />

    <!-- Files and Folders Section Header -->
    <div v-if="!isLoading" class="flex items-center justify-between">
      <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100">Files and Folders</h3>
      <span class="text-xs text-gray-600 dark:text-gray-400">
        {{ files.length }} {{ files.length === 1 ? 'item' : 'items' }}
      </span>
    </div>

    <!-- File list -->
    <div v-if="!isLoading && files.length > 0" class="border rounded-lg overflow-hidden">
      <table class="w-full text-sm">
        <thead class="bg-gray-50 dark:bg-gray-800">
          <tr>
            <th class="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Name</th>
            <th class="px-4 py-3 text-left font-medium text-gray-600 dark:text-gray-300">Size</th>
            <th class="px-4 py-3 text-right font-medium text-gray-600 dark:text-gray-300">
              Actions
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
          <tr
            v-for="item in files"
            :key="item.path"
            class="hover:bg-gray-50 dark:hover:bg-gray-800/50"
          >
            <td class="px-4 py-3">
              <button
                v-if="isFolder(item)"
                @click="handleFolderClick(item)"
                class="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                <Folder class="w-4 h-4" />
                {{ item.name }}
              </button>
              <div v-else class="flex items-center gap-2 text-gray-900 dark:text-gray-100">
                <File class="w-4 h-4 text-gray-400" />
                {{ item.name }}
              </div>
            </td>
            <td class="px-4 py-3 text-gray-600 dark:text-gray-400">
              {{ isFolder(item) ? '-' : formatSize(item.size) }}
            </td>
            <td class="px-4 py-3 text-right">
              <div class="flex items-center justify-end gap-1">
                <Button
                  v-if="!isFolder(item)"
                  variant="ghost"
                  size="sm"
                  @click="handleDownload(item)"
                  title="Download"
                >
                  <Download class="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  @click="handleDelete(item)"
                  class="text-red-600 hover:text-red-700 hover:bg-red-50"
                  title="Delete"
                >
                  <Trash2 class="w-4 h-4" />
                </Button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Empty state -->
    <div
      v-if="!isLoading && files.length === 0"
      class="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400"
    >
      <Folder class="w-12 h-12 mb-4 opacity-50" />
      <p>This folder is empty</p>
    </div>

    <!-- Create Folder Dialog -->
    <div
      v-if="showCreateFolderDialog"
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      @click.self="showCreateFolderDialog = false"
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <h3 class="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create New Folder</h3>
        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Folder Name
            </label>
            <input
              v-model="newFolderName"
              type="text"
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter folder name"
              :disabled="isCreatingFolder"
              @keydown.enter="createNewFolder"
            />
            <p v-if="folderError" class="mt-1 text-sm text-red-600">{{ folderError }}</p>
          </div>
        </div>
        <div class="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            @click="showCreateFolderDialog = false"
            :disabled="isCreatingFolder"
          >
            Cancel
          </Button>
          <Button @click="createNewFolder" :disabled="isCreatingFolder">
            <Loader2 v-if="isCreatingFolder" class="w-4 h-4 mr-2 animate-spin" />
            Create
          </Button>
        </div>
      </div>
    </div>

    <ConfirmDialog
      v-model:open="showDeleteDialog"
      title="Delete Item?"
      :description="`Are you sure you want to delete '${pendingDeleteItem?.name || 'this item'}'? This action cannot be undone.`"
      confirm-label="Delete"
      cancel-label="Cancel"
      variant="danger"
      @confirm="confirmDelete"
    />
  </div>
</template>
